import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const AboutUs: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user came from signup flow via query parameters
  const urlParams = new URLSearchParams(location.search);
  const fromSignup = urlParams.get('from') === 'signup';

  const handleBack = () => {
    if (fromSignup) {
      // Navigate back to signup if user came from signup flow
      navigate('/signup');
    } else if (window.history.length > 1) {
      // Use browser back if there's history
      window.history.back();
    } else {
      // Fallback to home page
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Professional Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <button
              onClick={handleBack}
              className="inline-flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors duration-200"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">{fromSignup ? 'Back' : 'Back'}</span>
              <span className="sm:hidden">Back</span>
            </button>
            <div className="text-center flex-1">
              <img 
                src="/images/noBgColor.png" 
                alt="Silas Logo" 
                className="h-6 sm:h-8 w-auto object-contain mx-auto"
              />
            </div>
            <div className="w-12 sm:w-16"></div> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-12">
        {/* Title Section */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">About Us – Silas</h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-2">
            Your mental health companion, designed for men who deserve support without judgment.
          </p>
        </div>

        {/* Content Grid */}
        <div className="bg-white rounded-lg sm:rounded-2xl shadow-xl border border-gray-200 overflow-hidden mx-1 sm:mx-0">
          <div className="px-4 py-6 sm:px-8 sm:py-12 lg:px-12 lg:py-16 prose prose-sm sm:prose-lg max-w-none">
            
            {/* Our Story Section */}
            <section className="mb-8 sm:mb-12">
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-black rounded-full mb-4">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Our Mission</h2>
              </div>
              
              <div className="space-y-4 sm:space-y-6 text-gray-700 leading-relaxed text-sm sm:text-base">
                <p>
                  At Silas, we believe every man deserves a safe, judgment-free space to talk, reflect, and grow. Too often, men struggle in silence—facing challenges they don't feel comfortable sharing with friends, family, or even professionals. That's where Silas steps in.
                </p>
                
                <p>
                  Silas is an AI-powered mental health companion designed to provide immediate support, guidance, and resources when you need them most. Whether it's stress, anxiety, relationships, or the weight of daily life, Silas listens, engages, and offers tools to help you navigate life's toughest moments.
                </p>
                
                <p>
                  Our mission is simple: to make mental health support accessible, private, and stigma-free for men everywhere. Silas isn't a replacement for therapy, but a bridge—a 24/7 companion that empowers you to take control of your mental well-being, start conversations, and move toward growth.
                </p>
                
                <div className="bg-black text-white p-4 sm:p-6 rounded-lg text-center mt-6 sm:mt-8">
                  <p className="text-base sm:text-lg font-medium">
                    With Silas, you're never alone.
                  </p>
                </div>
              </div>
            </section>


            {/* Important Notice */}
            <section className="mb-6 sm:mb-8">
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 sm:p-6 rounded-r-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700 font-medium leading-tight">
                      <strong>Important:</strong> Silas is designed to complement, not replace, professional mental health care. If you're experiencing a mental health crisis, please contact emergency services or a qualified healthcare provider immediately.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 sm:mt-12 px-4">
          <p className="text-xs sm:text-sm text-gray-500">
            © {new Date().getFullYear()} Silas. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
